import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Client } from 'app/models/Client';
import { ClientsService } from 'app/services/clients.service';
import { NotificationService } from 'app/services/notification.service';
import { LoadingService } from 'app/services/loading.service';
import { UserService } from 'app/services/user.service';
import { AvailableForm } from 'app/models/AvailableForm';
import * as _ from 'lodash';
import { StorageService } from 'app/services/storage.service';
import { UtilityService } from 'app/services/utility.service';
import { Configuration } from 'app/models/Configuration';
import { Title } from '@angular/platform-browser';
import { WorkflowGroupService } from 'app/services/workflowgroup.service';
import { ClientConfigurationView } from 'app/models/ClientConfigurationView';
import { WorkflowGroupDto } from 'app/models/WorkflowGroupDto';
import { forkJoin, Subject, switchMap, takeUntil } from 'rxjs';
import { FeatureFlagService } from 'app/services/feature-flag.service';
import { AvailableFormsService } from 'app/services/available-forms.service';
import { FEATURE_FLAGS } from 'app/shared/data/feature-flags';
import { EmailDomainWhitelistManagementService } from 'app/services/Administrator/email-domain-whitelist.service';
import { TabViewModule } from 'primeng/tabview';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SkeletonModule } from 'primeng/skeleton';
import { NgxMaskModule } from 'ngx-mask';
import { LogoUploadComponent } from 'app/shared/components/logo-upload/logo-upload.component';

class GroupedForms {
	name: string;
	items: AvailableForm[];
	id: string;

	constructor() {
		this.items = [];
	}
}

@Component({
	selector: 'app-modify',
	templateUrl: './client-modify.component.html',
	styleUrls: ['./client-modify.component.css'],
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		RouterModule,
		TabViewModule,
		SelectButtonModule,
		SkeletonModule,
		NgxMaskModule,
		LogoUploadComponent,
	],
})
export class ClientModifyComponent implements OnInit, OnDestroy {
	/**
	 * When provided, the component edits this client instead of reading the id from the route.
	 * Used by the slide-out Client Settings panel.
	 */
	@Input() clientId?: string;
	/** When true, the component is hosted in the slide-out panel: it never navigates and emits events instead. */
	@Input() isPanelMode = false;
	/** Emitted after the client information is successfully saved (so the host can refresh the client name). */
	@Output() saved = new EventEmitter<Client>();
	/** Emitted when the component wants its host (panel) to close. */
	@Output() requestClose = new EventEmitter<void>();

	private readonly destroy$ = new Subject<void>();

	clientConfigForm: UntypedFormGroup;
	clientConfigSubmitted: boolean;
	clientConfigItem: any;
	types: any[] = [
		{ label: 'Users', value: 'users' },
		{ label: 'Groups', value: 'groups' },
	];
	groups: WorkflowGroupDto[];
	isClientConfigInitialized: boolean = false;
	loadingConfigData = false;
	activeIndex = 0;
	groupedForms: GroupedForms[];
	clientForm: UntypedFormGroup;
	clientContactForm: UntypedFormGroup;
	clientTaxInfoForm: UntypedFormGroup;
	clientLogoDetailForm: UntypedFormGroup;
	submitted = false;
	item: Client = {
		enableMFA: false,
		passwordLength: 0,
		externalId: 0,
		id: '',
		name: '',
		description: '',
		webSiteUrl: '',
		isActive: true,
		passwordExpiryDays: 30,
		country: '',
		zipCode: '',
		state: '',
		city: '',
		addressLine2: '',
		address: '',
		disableRegularLogin: false,
	};
	groupedSections = [];
	forms: AvailableForm[];
	/** isEnabled per form id, captured after load/save; ngModel checkboxes are not reactive forms. */
	private initialFormEnabledById = new Map<string, boolean>();
	private reg = '(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?';
	formsAll = [];
	hrRequestAssignedTo: any = {};
	canViewPrivateLabel: boolean;
	users: any[] = [];
	configurations: Configuration[] = [];
	usStates = [];
	contactSubmitted: boolean;
	logoDetailSubmitted: boolean;
	taxInfoSubmitted: boolean;
	canGenerateTaxBandit = false;
	currentFile: File | null = null;
	previewUrl: string | null = null;
	standaloneProducts: { id: string; name: string; enabled: boolean; options: { id: string; name: string }[] }[] = [];
	standaloneUsers: { id: string; name: string; access: { productId: string; items: string[] }[] }[] = [];

	constructor(
		private formBuilder: UntypedFormBuilder,
		private clientService: ClientsService,
		private storageService: StorageService,
		public userService: UserService,
		private router: Router,
		private utilityService: UtilityService,
		private notifyService: NotificationService,
		private activatedRoute: ActivatedRoute,
		private loadingServices: LoadingService,
		private titleService: Title,
		private readonly workflowGroupService: WorkflowGroupService,
		private readonly featureFlagService: FeatureFlagService,
		private readonly availableFormsService: AvailableFormsService,
		private emailDomainWhitelistManagementService: EmailDomainWhitelistManagementService
	) {
		this.titleService.setTitle('Create Client');
		this.canViewPrivateLabel = this.userService.isAdmin();
		this.clientForm = this.formBuilder.group({
			name: [this.item.name, Validators.required],
			description: [this.item.description, Validators.maxLength(250)],
			externalId: [this.item.externalId, null],
			isActive: [this.item.isActive, null],
			enableMFA: [this.item.enableMFA, null],
			passwordLength: [this.item.passwordLength, Validators.minLength(8)],
			webSiteUrl: [this.item.webSiteUrl, Validators.pattern(this.reg)],
			passwordExpiryDays: [this.item.passwordExpiryDays, Validators.minLength(7)],
			address: [null, [Validators.maxLength(250), Validators.required]],
			addressLine2: [null, Validators.maxLength(100)],
			city: [null, [Validators.maxLength(100), Validators.required]],
			state: ['', [Validators.required]],
			zipCode: [null, [Validators.maxLength(10), Validators.required]],
			country: ['USA', Validators.required],
			disableRegularLogin: [this.item.disableRegularLogin, null],
		});

		this.clientContactForm = this.formBuilder.group({
			contactName: [null, [Validators.required, Validators.maxLength(100)]],
			contactType: [null],
			suffix: [null],
			title: [null, Validators.required],
			phone: [
				null,
				[Validators.required, Validators.pattern('^[0-9]*$'), Validators.minLength(10), Validators.maxLength(50)],
			],
			extension: [null, [Validators.maxLength(10)]],
			fax: [null, [Validators.pattern('^[0-9]*$'), Validators.minLength(10), Validators.maxLength(50)]],
			emailAddress: [null, [Validators.email, Validators.required, Validators.maxLength(250)]],
			additionalContactInfo: [null],
		});

		this.clientTaxInfoForm = this.formBuilder.group({
			einOrSSN: [null, [Validators.required, Validators.maxLength(11)]],
			isEIN: [true, Validators.required],
			businessType: [null, Validators.required],
			kindOfEmployer: [null, Validators.required],
			kindOfPayer: [null, Validators.required],
			signingAuthorityNm: [null],
			signingAuthorityPhnNum: [null],
			businessMemberType: [null],
			referenceId: [null],
			id: ['00000000-0000-0000-0000-000000000000'],
		});
		this.clientLogoDetailForm = this.formBuilder.group({
			logoUrl: [null],
			purchasingContactEmail: [null],
			purchasingAddress: [null],
			customerContactEmail: [null],
			customerContactAddress: [null],
			isBillingPortal: [false],
			isVendorPortal: [false],
			sendVendorRegistrationEmail: [true],
			vendorPortalUrl: [null],
			billingPortalUrl: [null],
		});

		this.clientConfigForm = this.formBuilder.group({
			vendorInvoiceCreation: [''],
			vendorInvoiceCreationType: ['users'],
			vendorInvoiceCreationGroupId: [''],

			vendorRegistrationCreation: [''],
			vendorRegistrationCreationType: ['users'],
			vendorRegistrationCreationGroupId: [''],

			vendorFormChangeRequest: [''],
			vendorFormChangeRequestType: ['users'],
			vendorFormChangeRequestGroupId: [''],

			businessLicensePortalUserId: [''],
			businessLicensePortalType: ['users'],
			businessLicensePortalGroupId: [''],

			customerBillingPortalUserId: [''],
			customerBillingPortalType: ['users'],
			customerBillingPortalGroupId: [''],

			purchasingPaymentsEnabled: [false],
			purchasingPaymentsDefaultValue: [false],

			checkServiceType: [''],

			reimbursementMileageRate: [''],
			travelMileageRate: [''],
		});

		this.formsAll = this.utilityService.getFormsAll();
		this.usStates = this.utilityService.getUsState();
	}

	get f() {
		return this.clientForm.controls;
	}

	get cf() {
		return this.clientContactForm.controls;
	}

	get ct() {
		return this.clientTaxInfoForm.controls;
	}
	get configForm() {
		return this.clientConfigForm.controls;
	}

	setForm() {
		this.titleService.setTitle(`Client Settings - ${this.item.name}`);
		this.clientForm.controls.name.setValue(this.item.name);
		this.clientForm.controls.description.setValue(this.item.description);
		this.clientForm.controls.externalId.setValue(this.item.externalId);
		this.clientForm.controls.isActive.setValue(this.item.isActive);
		this.clientForm.controls.enableMFA.setValue(this.item.enableMFA);
		this.clientForm.controls.passwordLength.setValue(this.item.passwordLength);
		this.clientForm.controls.webSiteUrl.setValue(this.item.webSiteUrl);
		this.clientForm.controls.passwordExpiryDays.setValue(this.item.passwordExpiryDays);
		this.clientForm.controls.address.setValue(this.item.address);
		this.clientForm.controls.addressLine2.setValue(this.item.addressLine2);
		this.clientForm.controls.city.setValue(this.item.city);
		this.clientForm.controls.state.setValue(this.item.state);
		this.clientForm.controls.zipCode.setValue(this.item.zipCode);
		this.clientForm.controls.country.setValue(this.item.country ?? 'USA');
		this.clientForm.controls.disableRegularLogin.setValue(this.item.disableRegularLogin);

		if (this.item.name == 'Aclarian') {
			this.clientService.getClientConfigurations(this.item.id).subscribe(
				(data) => {
					this.hrRequestAssignedTo = data.find((t) => t.configurationType == 'HRRequestAssignedTo') || {
						value: '',
						configurationType: 'HRRequestAssignedTo',
						clientId: this.item.id,
						id: 0,
					};
				},
				(error) => {
					console.error('Error occured', error);
				}
			);
		}
	}

	/** True when any of the client settings forms (or a pending logo upload) has unsaved edits. */
	get hasUnsavedChanges(): boolean {
		return this.unsavedChangesSummary.length > 0;
	}

	/** Human-readable labels for each unsaved edit (tabs and enabled-form checkboxes). */
	get unsavedChangesSummary(): string[] {
		const changes: string[] = [];
		if (this.clientForm.dirty) {
			changes.push('Client Information');
		}
		if (this.clientContactForm.dirty) {
			changes.push('Client Contact');
		}
		if (this.clientTaxInfoForm.dirty) {
			changes.push('Tax Information');
		}
		if (this.clientLogoDetailForm.dirty || this.currentFile) {
			changes.push('Client Logo/Module Contact Configuration');
		}
		for (const form of this.getChangedEnabledForms()) {
			const state = form.isEnabled ? 'enabled' : 'disabled';
			changes.push(`${form.section} – ${form.name} (${state})`);
		}
		return changes;
	}

	private captureFormEnabledState(): void {
		this.initialFormEnabledById = new Map(this.forms?.map((f) => [f.id, f.isEnabled]) ?? []);
	}

	private getChangedEnabledForms(): AvailableForm[] {
		if (!this.forms?.length || this.initialFormEnabledById.size === 0) {
			return [];
		}
		return this.forms.filter((f) => this.initialFormEnabledById.get(f.id) !== f.isEnabled);
	}

	ngOnInit(): void {
		// In panel mode the id arrives via @Input; otherwise fall back to the route param so the
		// existing /admin/clients/edit/:id flow is unchanged.
		this.item.id = this.clientId ?? this.utilityService.testId(this.activatedRoute.snapshot.paramMap.get('id'));
		if (this.item.id) {
			this.loadStandaloneAccess();
			this.loadingServices.OnLoading.emit(true);
			this.clientService.getClient(this.item.id).subscribe(
				(data) => {
					this.loadingServices.OnLoading.emit(false);
					this.item = data;
					this.item.externalId = +this.item.externalId; //cast as number
					this.setForm();
					this.canGenerateTaxBanditBusiness();
				},
				(error) => {
					this.loadingServices.OnLoading.emit(false);
					this.notifyService.showError(error, 'Error');
					if (this.isPanelMode) {
						this.requestClose.emit();
					} else {
						this.router.navigateByUrl('/admin/clients').then();
					}
				}
			);
			this.clientService.getClientContact(this.item.id).subscribe(
				(data) => {
					this.clientContactForm.patchValue(data);
					this.canGenerateTaxBanditBusiness();
				},
				(error) => {
					this.notifyService.showError(error, 'Error');
				}
			);
			this.clientService.getClientTaxInformation(this.item.id).subscribe(
				(data) => {
					this.clientTaxInfoForm.setValue(data);
					this.canGenerateTaxBanditBusiness();
				},
				(error) => {
					this.notifyService.showError(error, 'Error');
				}
			);

			this.clientService.getClientLogoConfiguration(this.item.id).subscribe(
				(data) => {
					this.clientLogoDetailForm.patchValue(data);
					this.previewUrl = data.logoUrl;
				},
				(error) => {
					this.notifyService.showError(error, 'Error');
				}
			);

			this.clientService.getClientForms(this.item.id).subscribe((data) => {
				this.forms = _.chain(data)
					.orderBy((t) => t.name)
					.value();
				this.groupedSections = _.chain(data.filter((x) => x.section))
					.groupBy((t) => t.section)
					.map((value, key) => {
						return { name: key, items: value };
					})
					.value();
				this.groupedForms = [];
				for (let group of this.groupedSections) {
					let forms = _.chain(group.items)
						.orderBy((t) => t.name)
						.value();
					let form = {
						name: group.name,
						items: [],
						id: '',
					};
					for (let item of forms) {
						if (item.section == group.name) {
							form.id = item.sectionId;
							form.items.push(item);
						}
					}
					this.groupedForms.push(form);
				}
				this.captureFormEnabledState();
			});
			this.featureFlagService
				.isEnabled(FEATURE_FLAGS.OPTIMIZED_AVAILABLE_FORMS)
				.pipe(
					switchMap((enabled) =>
						enabled
							? this.availableFormsService.getClientEnabledLegacyForms(this.item.id)
							: this.clientService.getClientForms(this.item.id)
					),
					takeUntil(this.destroy$)
				)
				.subscribe((data) => {
					this.forms = _.chain(data)
						.orderBy((t) => t.name)
						.value();
					this.groupedSections = _.chain(data.filter((x) => x.section))
						.groupBy((t) => t.section)
						.map((value, key) => {
							return { name: key, items: value };
						})
						.value();
					this.groupedForms = [];
					for (let group of this.groupedSections) {
						let forms = _.chain(group.items)
							.orderBy((t) => t.name)
							.value();
						let form = {
							name: group.name,
							items: [],
							id: '',
						};
						for (let item of forms) {
							if (item.section == group.name) {
								form.id = item.sectionId;
								form.items.push(item);
							}
						}
						this.groupedForms.push(form);
					}
					this.captureFormEnabledState();
				});
		} else {
			this.clientContactForm.disable();
			this.clientTaxInfoForm.disable();
			this.clientLogoDetailForm.disable();
		}

		this.setportalurls();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	canGenerateTaxBanditBusiness() {
		let contact = { ...this.clientContactForm.value };
		let taxInfo = { ...this.clientTaxInfoForm.value };
		let clientInfo = { ...this.clientForm.value };
		if (!this.item.id) {
			return false;
		}
		if (contact.contactName == null || contact.contactName == '') {
			return false;
		}
		if (clientInfo.address == null || clientInfo.address == '') {
			return false;
		}
		if (this.clientTaxInfoForm.valid) {
			this.canGenerateTaxBandit = taxInfo.referenceId == null || taxInfo.referenceId == '';
		}
		return this.canGenerateTaxBandit;
	}

	onClientFormSubmit() {
		this.submitted = true;
		// stop here if form is invalid
		if (this.clientForm.invalid) {
			this.utilityService.listErrors(this.clientForm);
			return;
		}

		this.loadingServices.OnLoading.emit(true);
		this.item = Object.assign(this.item, this.clientForm.value);

		if (this.item.id) {
			this.item.LastModifiedBy = this.userService.currentUserValue.user.email;
			this.clientService.updateClient(this.item).subscribe(
				(data) => {
					this.loadingServices.OnLoading.emit(false);
					this.item = data.client;
					this.item.externalId = +this.item.externalId;
					this.clientForm.markAsPristine();
					this.notifyService.showSuccess('Client Saved.', 'Success');
					this.canGenerateTaxBanditBusiness();
					this.saved.emit(this.item);
				},
				(error) => {
					this.loadingServices.OnLoading.emit(false);
					this.notifyService.showError(error, 'Error');
				}
			);
		} else {
			this.clientService.createClient(this.clientForm.value).subscribe(
				(data) => {
					this.loadingServices.OnLoading.emit(false);
					this.item = data.client;
					this.item.externalId = +this.item.externalId; // cast as number
					this.notifyService.showSuccess('Client Created.', 'Success');
					// Create is only reachable from the full-page route; the panel only edits existing clients.
					if (!this.isPanelMode) {
						this.router.navigate(['/admin/clients/edit', this.item.id]).then();
					}
				},
				(error) => {
					this.loadingServices.OnLoading.emit(false);
					this.notifyService.showError(error, 'Error');
				}
			);
		}
	}

	saveForms() {
		this.clientService.saveForms(this.item.id, this.forms).subscribe(() => {
			this.captureFormEnabledState();
			this.notifyService.showSuccess('Forms Saved.', 'Success');
		});
	}

	getSection(sectionId: string) {
		return this.storageService.dashboardSectionValue?.find((t) => t.name == sectionId);
	}

	updateSection(sectionId: string) {
		let section = this.getSection(sectionId);
		let array = this.storageService.dashboardSectionValue;
		if (section) {
			section.isShown = !section.isShown;
		} else {
			section = {
				isShown: true,
				name: sectionId,
			};
			if (!array) {
				array = [];
			}

			array.push(section);
		}

		this.storageService.updateSection(array);
	}

	hasItems(groupItems: any[], prop: string, value: string) {
		return groupItems.findIndex((x) => x[prop] === value) !== -1;
	}

	itemsOfType(groupItems: any[], type: string) {
		return (groupItems ?? []).filter((item) => item.type === type && item.id !== 'task-request');
	}

	onClientContactSubmit() {
		this.contactSubmitted = true;
		// stop here if form is invalid
		if (this.clientContactForm.invalid) {
			this.utilityService.listErrors(this.clientContactForm);
			return;
		}
		this.loadingServices.OnLoading.emit(true);
		let contact = { ...this.clientContactForm.value };
		this.clientService.updateClientContact(this.item.id, contact).subscribe(
			() => {
				this.canGenerateTaxBanditBusiness();
				this.clientContactForm.markAsPristine();
				this.loadingServices.OnLoading.emit(false);
				this.notifyService.showSuccess('Client Contact Saved.', 'Success');
			},
			(error) => {
				this.loadingServices.OnLoading.emit(false);
				this.notifyService.showError(error, 'Error');
			}
		);
	}

	onClientTaxInfoSubmit() {
		this.taxInfoSubmitted = true;
		// stop here if form is invalid
		if (this.clientTaxInfoForm.invalid) {
			this.utilityService.listErrors(this.clientTaxInfoForm);
			return;
		}
		this.loadingServices.OnLoading.emit(true);
		let taxInfo = { ...this.clientTaxInfoForm.value };
		this.clientService.updateClientTaxInformation(this.item.id, taxInfo).subscribe(
			() => {
				this.canGenerateTaxBanditBusiness();
				this.clientTaxInfoForm.markAsPristine();
				this.loadingServices.OnLoading.emit(false);
				this.notifyService.showSuccess('Client Tax Information Saved.', 'Success');
			},
			(error) => {
				this.loadingServices.OnLoading.emit(false);
				this.notifyService.showError(error, 'Error');
			}
		);
	}

	generateTaxBandit() {
		this.loadingServices.OnLoading.emit(true);
		this.clientService.createTaxBanditBusiness(this.item.id).subscribe(
			(data) => {
				this.clientTaxInfoForm.setValue(data);
				this.canGenerateTaxBanditBusiness();
				this.loadingServices.OnLoading.emit(false);
				this.notifyService.showSuccess('Tax Bandit Created.', 'Success');
			},
			(error) => {
				this.loadingServices.OnLoading.emit(false);
				this.notifyService.showError(error, 'Error');
			}
		);
	}

	onTabChange(index: number) {
		if (index === 3) {
			this.initializeConfig();
		}
	}

	initializeConfig() {
		if (!this.item?.id || this.isClientConfigInitialized) return;

		this.loadingConfigData = true;
		forkJoin([
			this.userService.getUsersForClient(this.item.id),
			this.workflowGroupService.getList(this.item.id),
			this.clientService.getClientFeatures(this.item.id),
		]).subscribe(([users, groups, clientConfig]) => {
			this.users = users;
			this.groups = groups;
			this.setClientConfigForm(clientConfig);
			this.loadingConfigData = false;
			this.isClientConfigInitialized = true;
		});
	}

	onClientConfigSubmit() {
		this.clientConfigSubmitted = true;
		if (this.clientConfigForm.valid) {
			this.clientConfigItem = { ...this.clientConfigForm.value };
			if (this.clientConfigItem.purchasingPaymentsEnabled === 'false') {
				this.clientConfigItem.purchasingPaymentsDefaultValue = 'false';
			}
			this.loadingServices.OnLoading.emit(true);
			// test
			this.clientService.updateAssignmentConfiguration(this.item.id, this.clientConfigItem).subscribe(
				(_) => {
					this.clientService.getClientFeatures(this.item.id).subscribe((res) => {
						localStorage.setItem('PurchasingPaymentsEnabled', res.purchasingPaymentsEnabled ?? 'false');
						localStorage.setItem('PurchasingPaymentsDefaultValue', res.purchasingPaymentsDefaultValue ?? 'false');
					});
					this.loadingServices.OnLoading.emit(false);
					this.notifyService.showSuccess('Assignment Configuration Saved!', 'Success');
				},
				(error) => {
					this.loadingServices.OnLoading.emit(false);
					this.notifyService.showError(error, 'Error');
				}
			);
		}
	}

	private setClientConfigForm(data: ClientConfigurationView) {
		this.clientConfigForm.reset();
		this.clientConfigItem = data;
		this.clientConfigForm.get('checkServiceType').setValue(data.checkServiceType || '');
		this.clientConfigForm.get('reimbursementMileageRate').setValue(data.reimbursementMileageRate || '');
		this.clientConfigForm.get('travelMileageRate').setValue(data.travelMileageRate || '');
		this.clientConfigForm.get('vendorInvoiceCreation').setValue(data.vendorInvoiceCreation || '');
		this.clientConfigForm
			.get('vendorInvoiceCreationGroupId')
			.setValue(data.vendorInvoiceCreationGroupId ? Number(data.vendorInvoiceCreationGroupId) : '');
		this.clientConfigForm
			.get('vendorInvoiceCreationType')
			.setValue(data.vendorInvoiceCreationGroupId ? 'groups' : 'users');

		this.clientConfigForm.get('vendorFormChangeRequest').setValue(data.vendorFormChangeRequest || '');
		this.clientConfigForm
			.get('vendorFormChangeRequestGroupId')
			.setValue(data.vendorFormChangeRequestGroupId ? Number(data.vendorFormChangeRequestGroupId) : '');
		this.clientConfigForm
			.get('vendorFormChangeRequestType')
			.setValue(data.vendorFormChangeRequestGroupId ? 'groups' : 'users');

		this.clientConfigForm.get('vendorRegistrationCreation').setValue(data.vendorRegistrationCreation || '');
		this.clientConfigForm
			.get('vendorRegistrationCreationGroupId')
			.setValue(data.vendorRegistrationCreationGroupId ? Number(data.vendorRegistrationCreationGroupId) : '');
		this.clientConfigForm
			.get('vendorRegistrationCreationType')
			.setValue(data.vendorRegistrationCreationGroupId ? 'groups' : 'users');

		this.clientConfigForm.get('customerBillingPortalUserId').setValue(data.customerBillingPortalUserId || '');
		this.clientConfigForm
			.get('customerBillingPortalGroupId')
			.setValue(data.customerBillingPortalGroupId ? Number(data.customerBillingPortalGroupId) : '');
		this.clientConfigForm
			.get('customerBillingPortalType')
			.setValue(data.customerBillingPortalGroupId ? 'groups' : 'users');

		this.clientConfigForm.get('businessLicensePortalUserId').setValue(data.businessLicensePortalUserId || '');
		this.clientConfigForm
			.get('businessLicensePortalGroupId')
			.setValue(data.businessLicensePortalGroupId ? Number(data.businessLicensePortalGroupId) : '');
		this.clientConfigForm
			.get('businessLicensePortalType')
			.setValue(data.businessLicensePortalGroupId ? 'groups' : 'users');

		this.clientConfigForm.get('purchasingPaymentsEnabled').setValue(data.purchasingPaymentsEnabled);
		this.clientConfigForm.get('purchasingPaymentsDefaultValue').setValue(data.purchasingPaymentsDefaultValue);
	}

	onVendorInvoiceCreationTypeChange() {
		if (this.configForm.vendorInvoiceCreationType.value === 'users') {
			this.clientConfigForm.get('vendorInvoiceCreationGroupId').setValue('');
		} else if (this.configForm.vendorInvoiceCreationType.value === 'groups') {
			this.clientConfigForm.get('vendorInvoiceCreation').setValue('');
		}
	}

	onVendorFormChangeRequestTypeChange() {
		if (this.configForm.vendorFormChangeRequestType.value === 'users') {
			this.clientConfigForm.get('vendorFormChangeRequestGroupId').setValue('');
		} else if (this.configForm.vendorFormChangeRequestType.value === 'groups') {
			this.clientConfigForm.get('vendorFormChangeRequest').setValue('');
		}
	}

	onVendorRegistrationCreationTypeChange() {
		if (this.configForm.vendorRegistrationCreationType.value === 'users') {
			this.clientConfigForm.get('vendorRegistrationCreationGroupId').setValue('');
		} else if (this.configForm.vendorRegistrationCreationType.value === 'groups') {
			this.clientConfigForm.get('vendorRegistrationCreation').setValue('');
		}
	}

	onCustomerBillingPortalTypeChange() {
		if (this.configForm.customerBillingPortalType.value === 'users') {
			this.clientConfigForm.get('customerBillingPortalGroupId').setValue('');
		} else if (this.configForm.customerBillingPortalType.value === 'groups') {
			this.clientConfigForm.get('customerBillingPortal').setValue('');
		}
	}

	onBusinessLicensePortalTypeChange() {
		if (this.configForm.businessLicensePortalType.value === 'users') {
			this.clientConfigForm.get('businessLicensePortalGroupId').setValue('');
		} else if (this.configForm.businessLicensePortalType.value === 'groups') {
			this.clientConfigForm.get('businessLicensePortalUserId').setValue('');
		}
	}
	onLogoSelected(file: any): void {
		if (file) {
			this.currentFile = file;
			const reader = new FileReader();
			reader.onload = () => {
				this.previewUrl = reader.result as string;
			};
			reader.readAsDataURL(file);
		} else {
			this.previewUrl = null;
		}
	}

	onClientLogoDetailSubmit() {
		this.logoDetailSubmitted = true;
		if (this.clientLogoDetailForm.invalid || (!this.currentFile && !this.previewUrl)) {
			if (!this.currentFile) {
				this.notifyService.showError('Logo is required.', 'Error');
			}

			return;
		}
		this.loadingServices.OnLoading.emit(true);
		let logoDetail = { ...this.clientLogoDetailForm.value };
		logoDetail.logo = this.currentFile;
		this.clientService.updateClientLogoDetail(this.item.id, logoDetail).subscribe(
			() => {
				this.loadingServices.OnLoading.emit(false);
				this.clientLogoDetailForm.markAsPristine();
				this.currentFile = null;
				this.notifyService.showSuccess('Client Logo Detail Saved.', 'Success');
			},
			(error) => {
				this.loadingServices.OnLoading.emit(false);
				this.notifyService.showError(error, 'Error');
			}
		);
	}

	setportalurls() {
		this.clientLogoDetailForm.get('isBillingPortal')?.valueChanges.subscribe((checked) => {
			const billingPortalUrlcontrol = this.clientLogoDetailForm.get('billingPortalUrl');
			if (checked) {
				billingPortalUrlcontrol?.setValidators([Validators.required]);
			} else {
				billingPortalUrlcontrol?.clearValidators();
				billingPortalUrlcontrol.setValue(null);
			}
			billingPortalUrlcontrol?.updateValueAndValidity();
		});
		this.clientLogoDetailForm.get('isVendorPortal')?.valueChanges.subscribe((checked) => {
			const vendorPortalUrlcontrol = this.clientLogoDetailForm.get('vendorPortalUrl');
			if (checked) {
				vendorPortalUrlcontrol?.setValidators([Validators.required]);
			} else {
				vendorPortalUrlcontrol?.clearValidators();
				vendorPortalUrlcontrol.setValue(null);
			}
			vendorPortalUrlcontrol?.updateValueAndValidity();
		});
	}
	loadStandaloneAccess() {
		if (!this.item?.id) {
			this.standaloneProducts = [];
			this.standaloneUsers = [];
			return;
		}
		this.userService.getStandaloneAccess(this.item.id).subscribe(
			(report) => {
				this.standaloneProducts = (report?.products ?? []).map((product) => ({
					id: product.id,
					name: product.name,
					enabled: !!product.enabled,
					options: product.items ?? [],
				}));
				this.standaloneUsers = report?.users ?? [];
			},
			() => {
				this.notifyService.showError('Unable to load standalone access', 'Error');
			}
		);
	}

	setStandaloneEnabled(product: { id: string }, enabled: boolean) {
		this.userService.setStandaloneProduct(this.item.id, product.id, enabled).subscribe(
			() => this.loadStandaloneAccess(),
			() => this.notifyService.showError('Unable to update the product', 'Error')
		);
	}

	usersWithStandaloneAccess() {
		return this.standaloneUsers.filter((person) =>
			this.standaloneProducts.some((product) => product.enabled && this.heldItems(person, product).length > 0)
		);
	}

	standaloneAccessLabel(
		person: { access: { productId: string; items: string[] }[] },
		product: { id: string; enabled: boolean; options: { id: string; name: string }[] }
	) {
		if (!product.enabled) {
			return 'Off for this client';
		}
		const held = this.heldItems(person, product);
		if (held.length === 0) {
			return 'No access';
		}
		return held.map((id) => product.options.find((option) => option.id === id)?.name ?? id).join(', ');
	}

	private heldItems(person: { access: { productId: string; items: string[] }[] }, product: { id: string }) {
		return person.access?.find((entry) => entry.productId === product.id)?.items ?? [];
	}

	manageEmailWhitelist() {
		if (!this.userService.isAdmin()) {
			this.notifyService.showError('Only admin users can manage email whitelist settings.', 'Error');
			return;
		}
		if (!this.item?.id) {
			return;
		}
		this.loadingServices.OnLoading.emit(true);
		this.emailDomainWhitelistManagementService.getByClientId(this.item.id).subscribe({
			next: (data) => {
				this.loadingServices.OnLoading.emit(false);
				if (!data) {
					this.router
						.navigate(['/admin/email-domain-whitelist-management/add'], {
							queryParams: { clientId: this.item.id },
						})
						.then();
					return;
				}
				this.router.navigate(['/admin/email-domain-whitelist-management/edit', data.id]).then();
			},
			error: (err) => {
				this.loadingServices.OnLoading.emit(false);
				this.notifyService.showError(err, 'Error');
			},
		});
	}
}
